import { Sidebar, type SidebarProps } from "primereact/sidebar";
import { type PropsWithChildren, createContext, useContext, useState } from "react";

interface SidebarOptions {
  body: React.ReactNode;
  position: "left" | "right" | "top" | "bottom";
  modal?: boolean;
  header?: React.ReactNode | ((props: SidebarProps) => React.ReactNode);
  dismissable?: boolean;
}

interface SidebarContextType {
  openSidebar: (options: SidebarOptions) => void;
  closeSidebar: () => void;
}

const ConfirmModalContext = createContext<SidebarContextType | undefined>(undefined);

export function useSidebar() {
  const value = useContext(ConfirmModalContext);
  if (!value) {
    throw new Error("useSidebar must be wrapped in a <SidebarProvider />");
  }
  return value;
}

export function SidebarProvider({ children }: PropsWithChildren) {
  const [visible, setVisible] = useState(false);
  const [sidebarOptions, setSidebarOptions] = useState<SidebarOptions>();

  const openSidebar = (options: SidebarOptions) => {
    setSidebarOptions(options);
    setVisible(true);
  };

  const closeSidebar = () => {
    setVisible(false);
    setSidebarOptions(undefined);
  };

  return (
    <ConfirmModalContext.Provider value={{ openSidebar, closeSidebar }}>
      {children}

      {sidebarOptions && (
        <Sidebar
          className="Sidebar"
          visible={visible}
          position={sidebarOptions.position}
          onHide={closeSidebar}
          modal={true}
          dismissable={sidebarOptions.dismissable ?? false}
          showCloseIcon={true}
          style={{ width: "490px" }}
          header={sidebarOptions.header}
        >
          {sidebarOptions.body}
        </Sidebar>
      )}
    </ConfirmModalContext.Provider>
  );
}
